/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import type {
  CalculationStore,
  LoanTerms,
} from '@hyphengroup/cms-components/calculatedAttributes/store';
import { CmsOffer } from '@hyphengroup/cms-components/types/cmsProduct';
import classNames from 'classnames';
import { MaterialIcon } from 'components/modulab/MaterialIcon';
import { useBreakpoint } from 'hooks/useBreakpoint';
import { useEffect, useRef, useState } from 'react';
import { trackLeadCreation } from 'utils/serverTracking';
import {
  BundleUpsellContext,
  trackApplyWithBundle,
  trackApplyWithoutBundle,
} from '../lib/bundleTracking';
import {
  ApplyStep,
  buildApplyRedirectUrl,
  buildApplySearch,
  getApplyNextStep,
} from '../lib/helpers';
import { useApplyLoan } from '../lib/useApplyLoan';
import { useApplyStepMeta } from '../lib/useApplyStepMeta';
import { getProductVertical } from '../utils';
import { ApplyCalculationsProvider } from './ApplyCalculationsProvider';
import { ApplyContent } from './ApplyContent';
import {
  ApplyEmailDraft,
  ApplyEmailFooter,
  ApplyEmailFooterSubmitPayload,
} from './ApplyEmailFooter';
import { ApplyTrackingMeta } from './ApplyTrackingMeta';
import { BundleUpsellContent } from './BundleUpsellContent';
import { BundleUpsellFooter } from './BundleUpsellFooter';
import {
  SendLeadToServiceBus,
  createLead,
} from '$/(application)/apply/[productId]/server-actions';
import { ApplyStaticText } from '$/(cms)/-cms/apply-v3/[[...slugs]]/i18n';
import { ApplyViewMode } from '$/(cms)/-cms/apply-v3/[[...slugs]]/lib/types';
import { CmsApplyRewardProduct } from '$/(cms)/_modules/cmsService/types/applyRewardProduct';
import { ApplyMode } from '$/(cms)/_modules/routing';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type Props = {
  mode: ApplyMode;
  viewMode: ApplyViewMode;
  product: CmsApplyRewardProduct;
  initialNow: number;
  /**
   * The loan of the CTA that opened the modal (CMS-2052); `useApplyLoan` turns
   * it into what the form prices and sends. The page form has none: its URL
   * already carries it.
   */
  loan?: LoanTerms;
  onClose?: () => void;
  /**
   * Calculated attributes the server already worked out, with the market
   * defaults (CMS-1959). Absent in the modal, which fetches its product on the
   * client — the provider fills the slot itself there.
   */
  calculations?: CalculationStore;
  /** Preview: the client recalculates against the same draft data as the server. */
  isDraft: boolean;
};

export function ApplyForm({
  mode,
  viewMode,
  product,
  initialNow,
  loan,
  onClose,
  calculations,
  isDraft,
}: Props) {
  const context = useClientContext();
  const { langPrefix } = context;
  const staticText = getClientTranslations(ApplyStaticText, context);
  const productName = product.catalogueProduct?.product_name ?? '';
  const heading = staticText.applyingForCard.replace(
    ':productName',
    productName,
  );
  const showCloseButton = viewMode === ApplyViewMode.Modal;
  const isPage = viewMode === ApplyViewMode.Page;

  // Page (M2/M3): header / content / sticky-bar are full-width bands; their
  // inner content sits in a centered column capped at narrow-content-max-width-2
  // (720px @ ≥768, 960px @ ≥1280 — responsive token, per design CMS-1740).
  // Applies to the bundle step too (content + footer share this band). Modal:
  // content fills the dialog, so no inner cap.
  const bandInner = isPage
    ? 'mx-auto w-full md:max-w-general/narrow-content-max-width-2'
    : 'w-full';

  const [step, setStep] = useState<ApplyStep>('capture');
  const applyLoan = useApplyLoan(loan, viewMode);

  // On step change (e.g. capture → bundle-upsell) reset the scroll to the top
  // of the flow — otherwise the user stays scrolled at the previous position.
  // Skips the initial mount. Works for both page (window) and modal (dialog).
  const rootRef = useRef<HTMLDivElement>(null);
  const isInitialStep = useRef(true);
  useEffect(() => {
    if (isInitialStep.current) {
      isInitialStep.current = false;
      return;
    }
    rootRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [step]);

  // Desktop modal capture step renders the auth form inline (in the content
  // flow, before the legal text); the mobile sheet keeps the sticky footer.
  const isDesktop = useBreakpoint({ _: false, md: true });
  const inlineAuth =
    viewMode === ApplyViewMode.Modal && isDesktop && step === 'capture';
  // Crossing md (a phone turning to landscape) moves the form between the two
  // places and remounts it; what the reader entered is kept here.
  const emailDraft = useRef<ApplyEmailDraft>({});
  const [selectedBundleOffer, setSelectedBundleOffer] = useState<CmsOffer>();
  const [selectedBundleIndex, setSelectedBundleIndex] = useState<number>();
  const [bundleUpsellContext, setBundleUpsellContext] =
    useState<BundleUpsellContext>();
  const { leadId, stepName, stepNumber } = useApplyStepMeta(
    step,
    bundleUpsellContext,
  );

  const redirectToProvider = (useSecondUrlSet = false) => {
    if (!product.productId) return;
    window.location.href = buildApplyRedirectUrl(
      langPrefix,
      product.productId,
      buildApplySearch(window.location.search, applyLoan.sent),
      useSecondUrlSet,
    );
  };

  const handleEmailSubmit = async ({
    email,
    marketingConsent,
    loginMethod,
  }: ApplyEmailFooterSubmitPayload) => {
    const nextStep = getApplyNextStep(product);
    const itemId = product.catalogueProduct?.uid ?? product.productUid ?? '';
    const itemName =
      product.catalogueProduct?.product_name ??
      product.productDisplayName ??
      '';
    const itemBrand = product.catalogueProduct?.provider?.provider_name ?? '';
    const rewardCampaignName =
      product.productName ?? product.productDisplayName ?? itemName;
    const verticalCode =
      product.productVertical?.code ?? itemId.split('.')[1] ?? '';
    const nextBundleUpsellContext: BundleUpsellContext = {
      email,
      marketingConsent,
      loginMethod,
    };

    try {
      if (!product.productId) throw new Error('Missing productId');
      const requestSource = ['MEMBERSHIP-V3'];
      requestSource.push(`met-${loginMethod}`);

      const lead = await createLead({
        email,
        productId: product.productId,
        consent: marketingConsent,
        searchParams: buildApplySearch(window.location.search, applyLoan.sent),
        requestSource: requestSource.join(','),
      });

      setBundleUpsellContext({
        ...nextBundleUpsellContext,
        leadId: String(lead.leadId),
      });

      await SendLeadToServiceBus({
        External_Lead_Id: lead.leadId.toString(),
        Email: email,
        Last_Name: email || '-',
        Product_Type: product.productVertical?.name ?? '',
        Marketing_Consent: marketingConsent,
        Selected_Product: itemName,
        Selected_Provider: itemBrand,
        Card_Application_Link: lead.redemptionUrl,
      });

      await trackLeadCreation({
        email,
        vertical: verticalCode,
        item_brand: itemBrand,
        item_id: itemId,
        item_name: itemName,
        reward_lead_id: lead.leadId,
        reward_campaign_name: rewardCampaignName,
        reward_campaign_id: product.productId,
        login_method: loginMethod,
        document_location_url: window.location.href,
      });
    } catch {
      setBundleUpsellContext(nextBundleUpsellContext);
      // TODO CMS-1673: replace silent fallback with explicit UX once apply-v3
      // lead submission error handling is defined.
    }

    if (nextStep === 'bundle-upsell') {
      setStep('bundle-upsell');
      return;
    }
    redirectToProvider();
  };

  const handleBundleApply = (ctaLabel: string) => {
    if (
      selectedBundleOffer &&
      selectedBundleIndex != null &&
      bundleUpsellContext
    ) {
      trackApplyWithBundle(
        product,
        selectedBundleOffer,
        selectedBundleIndex,
        bundleUpsellContext,
        ctaLabel,
      );
    }
    redirectToProvider();
  };

  const handleApplyWithoutBundle = (ctaLabel: string) => {
    if (bundleUpsellContext) {
      trackApplyWithoutBundle(product, bundleUpsellContext, ctaLabel);
    }
    redirectToProvider(true);
  };

  const emailFooter = (
    <ApplyEmailFooter
      mode={mode}
      viewMode={viewMode}
      onSubmit={handleEmailSubmit}
      product={product}
      draft={emailDraft}
    />
  );

  return (
    // At form level so every surface here — the card, the bundles, the reward
    // catalogue — reads one slot (CMS-1959).
    <ApplyCalculationsProvider
      product={product.catalogueProduct}
      vertical={
        product.catalogueProduct?.title
          ? getProductVertical(product.catalogueProduct.title)
          : undefined
      }
      terms={applyLoan.priced}
      serverStore={calculations}
      isDraft={isDraft}
    >
      <ApplyTrackingMeta
        leadId={leadId}
        product={product}
        stepName={stepName}
        stepNumber={stepNumber}
      />

      <div
        ref={rootRef}
        className="flex w-full flex-1 flex-col bg-fill/card"
        data-component="apply-v3-form"
      >
        <header
          className={classNames(
            'shrink-0 border-b border-borders/default',
            // The mobile sheet scrolls as a whole: keep the close button in view.
            // Mobile-first, because `max-*` variants are off in this Tailwind config.
            showCloseButton && 'sticky top-0 z-10 bg-fill/card md:static',
          )}
        >
          <div
            className={classNames(
              'flex items-center gap-spacing/gap/x-sm px-spacing/padding/x py-spacing/padding/y-sm md:py-spacing/padding/y',
              bandInner,
            )}
          >
            <h1
              className={classNames(
                'line-clamp-2 min-w-0 flex-1 text-left font-semibold text-text/primary text-base leading-5 md:truncate md:text-xl md:leading-6',
                // Modal (M1): centered heading. Page (M2/M3): left-aligned per Figma.
                showCloseButton && 'md:text-center',
              )}
            >
              {heading}
            </h1>
            {showCloseButton && onClose ? (
              <button
                aria-label="Close"
                className="flex size-[22px] shrink-0 items-center justify-center text-text/primary"
                onClick={onClose}
                type="button"
              >
                <MaterialIcon icon="close" px={16} />
              </button>
            ) : null}
          </div>
        </header>

        <section className="flex flex-1 flex-col md:min-h-0 md:overflow-y-auto">
          <div
            className={classNames(
              'flex flex-1 flex-col gap-spacing/gap/section-y px-spacing/padding/section-x py-spacing/padding/section-y [&>*]:shrink-0',
              bandInner,
            )}
          >
            {step === 'capture' ? (
              <ApplyContent
                mode={mode}
                viewMode={viewMode}
                product={product}
                initialNow={initialNow}
                authSlot={inlineAuth ? emailFooter : undefined}
              />
            ) : (
              <BundleUpsellContent
                product={product}
                selectedOffer={selectedBundleOffer}
                onSelectionChange={(offer, index) => {
                  setSelectedBundleOffer(offer);
                  setSelectedBundleIndex(index);
                }}
                trackingContext={bundleUpsellContext}
              />
            )}
          </div>
        </section>

        {!inlineAuth && (
          // Sticky footer band: top divider + 6px upward shadow. Surface fill
          // differs by step — the capture-step auth bar uses fill/base to stand
          // out from the body (CMS-1740 AC#6); the bundle-upsell footer sits
          // flush on the white container (fill/card) per design (CMS-1816).
          <div
            className={classNames(
              'sticky bottom-0 z-10 w-full border-t border-borders/default shadow-[0_0_6px_0_rgba(0,0,0,0.25)]',
              step === 'capture' ? 'bg-fill/base' : 'bg-fill/card',
            )}
          >
            <div
              className={classNames(
                'px-spacing/padding/x py-spacing/padding/y md:px-spacing/padding/section-x',
                bandInner,
              )}
            >
              {step === 'capture' ? (
                emailFooter
              ) : (
                <BundleUpsellFooter
                  selectedOffer={selectedBundleOffer}
                  onApplyWithBundle={handleBundleApply}
                  onApplyWithoutBundle={handleApplyWithoutBundle}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </ApplyCalculationsProvider>
  );
}
